import { FC, ReactNode } from 'react';
import { Section, Link } from 'components';
import { font, color } from 'style';

import S from './StepsSection.Styled';

export interface StepProp {
  title: ReactNode;
  list: string[];
  isLast?: boolean;
}

const Step: FC<StepProp> = ({ title, list, isLast = false }) => {
  return (
    <S.Step>
      <S.StepLine>
        <S.Circle />
        <S.Line isDot={isLast} />
      </S.StepLine>
      <S.Content>
        <S.StepTitle fontSize="17px">{title}</S.StepTitle>
        <S.StepList>
          {list.map((item) => (
            <S.StepListItem key={item}>{item}</S.StepListItem>
          ))}
        </S.StepList>
      </S.Content>
    </S.Step>
  );
};

interface StepsSectionProps {
  backgroundColor?: string;
  projectName: string;
  title: string;
  subTitle?: string;
  faqLink?: string;
  description?: ReactNode;
  steps?: StepProp[];
  firstStep?: StepProp;
  secondStep?: StepProp;
  thirdStep?: StepProp;
  applyLink?: string;
  disableApply?: boolean;
  unavailableContent?: ReactNode;
  onApply?: () => void;
}

export const StepsSection: FC<StepsSectionProps> = ({
  backgroundColor,
  projectName,
  title,
  subTitle,
  faqLink,
  description,
  steps,
  firstStep,
  secondStep,
  thirdStep,
  applyLink,
  disableApply,
  unavailableContent,
  onApply,
}) => {
  const stepList = steps ?? [
    firstStep ?? {
      title: (
        <span>
          Διαδικασία αιτήσεων
          <br />
          <i>(09/02/2024 - 25/03/2024)</i>
        </span>
      ),
      list: ['Online αίτηση', 'Mini interview', 'Επιλογή συμμετεχουσών'],
    },
    secondStep ?? {
      title: (
        <span>
          Διεξαγωγή Επιμόρφωσης και Mentoring
          <br />
          <i>(01/04/2023 - 30/06/2024)</i>
        </span>
      ),
      list: [
        '4-10 ώρες επιμόρφωσης ανά εβδομάδα',
        '6 ώρες εργαστηρίων ανάπτυξης δεξιοτήτων',
        '20 - 40 λεπτά mentoring ανά εβδομάδα',
      ],
    },
    thirdStep ?? {
      title: <span>Προϋποθέσεις για την πιστοποίηση EQUALL {projectName}</span>,
      list: [
        'Παρακολούθηση του 70% των διδασκόμενων ωρών της επιμόρφωσης και των εργαστηρίων δεξιοτήτων',
        'Απόκτηση του GenAI Explorer Badge μέσω της εφαρμογής 100mentors.AI',
        'Συμπλήρωση ερωτηματολογίων αξιολόγησης του προγράμματος',
      ],
    },
  ];

  return (
    <Section backgroundColor={backgroundColor}>
      <S.Container>
        <S.Title>{title}</S.Title>
        {subTitle && <S.SubTitle fontSize={font.size.large}>{subTitle}</S.SubTitle>}
        <S.StepContainer>
          {stepList?.map((step, index) => (
            <Step key={step.list[0]} {...step} isLast={index === stepList.length - 1} />
          ))}
        </S.StepContainer>
        <S.Description>
          {description}
          {faqLink && (
            <S.FAQ>
              Έχετε περισσότερες απορίες; Βρείτε τις συχνές ερωτήσεις (FAQ){' '}
              <Link href={faqLink} targetBlank>
                εδώ
              </Link>
              .
            </S.FAQ>
          )}
          {applyLink && onApply && (
            <S.ApplyButton
              primaryColor={color.hmBrandPurple}
              secondaryColor={color.white}
              href={applyLink}
              unavailable={disableApply}
              unavailableContent={unavailableContent}
              onClick={onApply}
            />
          )}
        </S.Description>
      </S.Container>
    </Section>
  );
};
